import { CultevaChartTypes } from "../report";

export enum ChartXaxisOption {
    TIME = 'TIME',
    COUNT = 'COUNT',
}

type axis = 'x' | 'y';
type position = 'top' | 'bottom' | 'left' | 'right';

interface ChartTitle {
    display: boolean;
    text: string;
    font: {
        weight: string;
        size: number;
    }
}

interface ChartPlugins {
    legend?: {
        display: boolean;
    }
    title?: {
        display: boolean;
        text: string;
        position: position;
        font: {
            weight: string;
            size: number;
        };
    }
}

interface ChartTooltips {
    mode: string;
    axis?: axis;
    enabled?: boolean;
}

interface ChartGrid {
    color?: any;
    display: boolean;
}

export interface BarChartConfig {
    plugins?: ChartPlugins
    tooltips: ChartTooltips
    scales: {
        x: {
            ticks: {
                maxRotation: number;
                minRotation: number;
            }
            stacked: false;
            title: ChartTitle;
            grid: ChartGrid;
        };
        y: {
            stacked: boolean;
            title: ChartTitle;
            grid: ChartGrid;
            ticks: {
                suggestedMin: number;
            },
            beginAtZero: boolean;
            grace: string;
        }
    },
    responsive: boolean;
}

export interface LineChartConfig {
    plugins?: ChartPlugins
    scales: {
        x: {
            ticks: {
                maxRotation: number;
                minRotation: number;
                align: "start" | "center" | "end";
            };
            title: ChartTitle;
            grid: ChartGrid;
            offset: boolean;
        };
        y: {
            title: ChartTitle;
            grid: ChartGrid;
            ticks: {
                suggestedMin: number;
            };
            offset: boolean;
            beginAtZero: boolean;
            grace: string;
        }
    },
    responsive: boolean;
}

export interface BoxplotChartConfig {
    tooltipDecimals: number;
    plugins?: ChartPlugins;
    tooltips: ChartTooltips;
    scales: {
        x: {
            type: string;
            title: ChartTitle;
            grid: ChartGrid;
            ticks: {
                maxRotation: number;
            };
        };
        y: {
            title: ChartTitle;
            ticks: {
                suggestedMin: number;
            };
            grid: ChartGrid;
            offset: boolean;
            beginAtZero: boolean;
            grace: string;
        };
    }
    responsive: boolean;
}

export interface ScatterPlotCongig {
    plugins?: ChartPlugins;
    scales: {
        x: {
            type: string;
            position: position;
            title: ChartTitle;
            grid: ChartGrid;
        };
        y: {
            title: ChartTitle;
            grid: ChartGrid;
            offset: boolean;
            beginAtZero: boolean;
            grace: string;
        };
    }
    elements: {
        point: {
            radius: number;
            hoverRadius: number;
        }
    }
}

export interface BubbleChartConfig {
    plugins?: ChartPlugins;
    scales: {
        x: {
            type: string;
            position: position;
            title: ChartTitle;
            grid: ChartGrid;
            offset: boolean;
        };
        y: {
            title: ChartTitle;
            grid: ChartGrid;
            offset: boolean;
            beginAtZero: boolean;
            grace: string;
        };
    }

}

export const BAR_CHART_DEFAULT_CONFIG: BarChartConfig = {
    plugins: {
        legend: {
            display: true,
        },
        title: {
            display: false,
            text: "",
            position: "top",
            font: {
                weight: 'bold',
                size: 15,
            }
        },
    },
    tooltips: {
        mode: 'index',
        axis: 'y'
    },
    scales: {
        x: {
            ticks: {
                maxRotation: 45,
                minRotation: 45,
            },
            stacked: false,
            title: {
                display: true,
                text: "X-Axis",
                font: {
                    weight: 'bold',
                    size: 14,
                }
            },
            grid: {
                display: false,
            },
        },
        y: {
            stacked: false,
            title: {
                display: true,
                text:"Y-Axis",
                font: {
                    weight: 'bold',
                    size: 14,
                }
            },
            grid: {
                display: true,
            },
            ticks:{
                suggestedMin: 0
            },
            beginAtZero: false,
            grace: "10%",
        }
    },
    responsive: true,
}

export const LINE_CHART_DEFAULT_CONFIG: LineChartConfig = {
    plugins: {
        legend: {
            display: true,
        },
        title: {
            display: false,
            text: "",
            position: "top",
            font: {
                weight: 'bold',
                size: 15,
            }
        },
    },
    scales: {
        x: {
            title: {
                display: true,
                text: "",
                font: {
                    weight: 'bold',
                    size: 14,
                }
            },
            ticks: {
                maxRotation: 45,
                minRotation: 45,
                align: "start"
            },
            grid: {
                display: false,
            },
            offset: true,
        },
        y: {
            title: {
                display: true,
                text: "",
                font: {
                    weight: 'bold',
                    size: 14,
                }
            },
            grid: {
                display: true,
            },
            ticks: {
                suggestedMin: 0
            },
            offset: false,
            beginAtZero: false,
            grace: "10%",
        }
    },
    responsive: true,
}

export const BOXPLOT_CHART_DEFAULT_CONFIG: BoxplotChartConfig = {
    tooltipDecimals: 2,
    plugins: {
        legend: {
            display: true,
        },
        title: {
            display: false,
            text: "",
            position: "top",
            font: {
                weight: 'bold',
                size: 15,
            }
        },
    },
    tooltips: {
        enabled: true,
        mode: 'nearest',
    },
    scales: {
        x: {
            type: 'category',
            title: {
                display: true,
                text: "",
                font: {
                    weight: 'bold',
                    size: 14,
                },
            },
            grid: {
                display: false,
            },
            ticks: {
                maxRotation: 45,
            },
        },
        y: {
            title: {
                display: true,
                text: "",
                font: {
                    weight: 'bold',
                    size: 14,
                }
            },
            ticks: {
                suggestedMin: 40
            },
            grid: {
                display: true,
            },
            offset: true,
            beginAtZero: false,
            grace: "10%",
        }
    },
    responsive: true,
}

export const SCATTER_CHART_DEFAULT_CONFIG: ScatterPlotCongig = {
    plugins: {
        legend: {
            display: true,
        },
    },
    scales: {
        x: {
            type: 'linear',
            position: 'bottom',
            title: {
                display: true,
                text: "",
                font: {
                    weight: 'bold',
                    size: 14,
                }
            },
            grid: {
                display: false,
            },
        },
        y: {
            title: {
                display: true,
                text: "",
                font: {
                    weight: 'bold',
                    size: 14,
                },
            },
            grid: {
                display: true,
            },
            offset: true,
            beginAtZero: false,
            grace: "10%",
        }
    },
    elements: {
        point: {
            radius: 5,
            hoverRadius: 7,
        }
    }
}

export const BUBBLE_CHART_DEFAULT_CONFIG: BubbleChartConfig = {
    plugins: {
        legend: {
            display: true,
        },
        title: {
            display: false,
            text: "",
            position: "top",
            font: {
                weight: 'bold',
                size: 15,
            }
        },
    },
    scales: {
        x: {
            type: 'linear',
            position: "bottom",
            title: {
                display: true,
                text: "",
                font: {
                    weight: 'bold',
                    size: 14,
                }
            },
            grid: {
                display: false,
            },
            offset: true,
        },
        y: {
            title: {
                display: true,
                text: "",
                font: {
                    weight: 'bold',
                    size: 14,
                }
            },
            grid: {
                display: true,
            },
            offset: true,
            beginAtZero: false,
            grace: "10%",
        },
    },
}

export const getDefaultChartConfig = (type: keyof CultevaChartTypes): BarChartConfig | LineChartConfig | BoxplotChartConfig | ScatterPlotCongig | BubbleChartConfig | null => {
    if (type === 'bar') return BAR_CHART_DEFAULT_CONFIG;
    if (type === 'line') return LINE_CHART_DEFAULT_CONFIG;
    if (type === 'boxplot') return BOXPLOT_CHART_DEFAULT_CONFIG;
    if (type === 'scatter') return SCATTER_CHART_DEFAULT_CONFIG;
    if (type === 'bubble') return BUBBLE_CHART_DEFAULT_CONFIG;

    return null;
}

// TODO CHECK IF THIS ANY TYPE BREAKS FOR OTHER CHART TYPES
export const setChartConfigOptions = (type: keyof CultevaChartTypes, config: any, xAxisLabel: string, yAxisLabel: string, chartTitle?: string) => {
    let defaultConfig = getDefaultChartConfig(type);

    chartTitle = chartTitle || null;

    defaultConfig.plugins = {
        ...defaultConfig.plugins,
        title: {
            ...defaultConfig.plugins.title,
            display: chartTitle != null ? true : false,
            text: chartTitle,
        }
    }

    return {
        ...defaultConfig,
        ...config,
        scales: {
            ...config.scales,
            x: {
                ...config.scales.x,
                title: {
                    ...config.scales.x.title,
                    text: xAxisLabel,
                }
            },
            y: {
                ...config.scales.y,
                title: {
                    ...config.scales.y.title,
                    text: yAxisLabel,
                }
            },
        }
    }
}